import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AiOutlineWallet } from "react-icons/ai";
import { getDatabase, ref, push, update, onValue } from "firebase/database";
import { auth } from "../firebase/firebase";
import Modal from "../components/Modal";

export default function Withdraw() {
  const [walletAddress, setWalletAddress] = useState("");
  const [amount, setAmount] = useState("");
  const [balance, setBalance] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const navigate = useNavigate();

  // Fetch the user's balance in real-time
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const db = getDatabase();
    const balanceRef = ref(db, `users/${user.uid}/balance`);

    const unsubscribe = onValue(balanceRef, (snapshot) => {
      if (snapshot.exists()) {
        setBalance(Number(snapshot.val()) || 0);
      }
    });

    return () => unsubscribe(); // Cleanup listener on unmount
  }, []);

  // Handle withdrawal request
  const handleWithdraw = async () => {
    if (!walletAddress.trim() || !amount.trim()) {
      setModalMessage("Please fill in all fields.");
      setShowModal(true);
      return;
    }

    const withdrawAmount = parseFloat(amount);
    if (isNaN(withdrawAmount) || withdrawAmount < 100) {
      setModalMessage("Minimum withdrawal is $100.");
      setShowModal(true);
      return;
    }
    if (withdrawAmount > balance) {
      setModalMessage("Insufficient balance.");
      setShowModal(true);
      return;
    }

    const user = auth.currentUser;
    if (user) {
      const db = getDatabase();
      const userWithdrawalsRef = ref(db, `users/${user.uid}/withdrawals`);
      const userBalanceRef = ref(db, `users/${user.uid}`);

      // Create a structured withdrawal entry
      const newWithdrawal = {
        walletAddress,
        usdAmount: Number(withdrawAmount), // Ensure it's stored as a number
        status: "Pending",
        date: new Date().toISOString(),
      };

      await push(userWithdrawalsRef, newWithdrawal);

      // Deduct balance after successful withdrawal request
      const newBalance = balance;
      if (newBalance >= 0) {
        await update(userBalanceRef, { balance: newBalance });
        setBalance(newBalance);
      }

      setModalMessage("Withdrawal processing...");
      setShowModal(true);
      setTimeout(() => {
        setShowModal(false);
        navigate("/dashboard");
      }, 2000);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-screen bg-gray-100">
      <div className="bg-white p-6 rounded-lg shadow-md w-96">
        <h2 className="text-xl font-bold text-center mb-4">Withdraw Funds</h2>
        <div className="mb-4">
          <label className="block text-sm font-medium">
            Bitcoin Wallet Address
          </label>
          <input
            type="text"
            value={walletAddress}
            onChange={(e) => setWalletAddress(e.target.value)}
            className="w-full p-2 border rounded mt-1"
            placeholder="Enter BTC wallet address"
          />
          <p className="text-red-500 text-xs mt-1">
            Ensure the wallet address is correct. Lost coins cannot be
            recovered.
          </p>
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium">Amount ($)</label>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full p-2 border rounded mt-1"
            placeholder="Enter amount"
          />
          {amount && parseFloat(amount) > balance && (
            <p className="text-red-500 text-sm mt-1">
              Amount exceeds available balance!
            </p>
          )}
        </div>
        <button
          onClick={handleWithdraw}
          className="w-full bg-blue-500 text-white p-2 rounded hover:bg-blue-600 flex items-center justify-center gap-2"
        >
          <AiOutlineWallet size={20} /> Withdraw
        </button>
      </div>
      {showModal && (
        <Modal message={modalMessage} onClose={() => setShowModal(false)} />
      )}
    </div>
  );
}
